"use client";

import { CheckCircle2 } from "lucide-react";
import Image from "next/image";
import type { FormEvent } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { ReaderReportType } from "@/lib/reports/report-type";

interface NewsletterSubscribeFormProps {
  email: string;
  onEmailChange: (value: string) => void;
  onSubscribe: (options: { editions: ReaderReportType[] }) => void;
  currentEdition: ReaderReportType;
  isSubmitting: boolean;
  isError: boolean;
  errorMessage?: string;
  isSuccess: boolean;
  alreadySubscribed?: boolean;
}

export function NewsletterSubscribeForm({
  email,
  onEmailChange,
  onSubscribe,
  currentEdition,
  isSubmitting,
  isError,
  errorMessage,
  isSuccess,
  alreadySubscribed,
}: NewsletterSubscribeFormProps) {
  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    onSubscribe({ editions: [currentEdition] });
  };

  return (
    <div
      className="text-center"
      style={{ fontFamily: "var(--homepage-sans-stack)" }}
    >
      <div className="mx-auto mb-3 h-16 w-16 overflow-hidden rounded-full">
        <Image
          alt="AI Primer mascot"
          className="h-full w-full object-cover"
          height={64}
          src="/logo.webp"
          width={64}
        />
      </div>

      {isSuccess ? (
        <div className="flex flex-col items-center gap-1.5 py-1">
          <CheckCircle2 className="h-5 w-5 text-[var(--explore-success-text)]" />
          <span className="font-semibold text-[0.85rem] text-[var(--explore-success-text)]">
            {alreadySubscribed ? "Already Subscribed!" : "Subscribed!"}
          </span>
        </div>
      ) : (
        <>
          <h4 className="mb-1 font-semibold text-[0.95rem] text-[var(--explore-text-primary)]">
            Daily AI Digest
          </h4>
          <p className="mb-3 text-[0.75rem] text-[var(--explore-text-muted)]">
            Get the best stories delivered
            <br />
            to your inbox
          </p>
          <form onSubmit={handleSubmit}>
            <Input
              className="mb-2 border-[var(--explore-border)] bg-[var(--explore-bg-card)] text-center text-[0.8rem]"
              disabled={isSubmitting}
              onChange={(e) => onEmailChange(e.target.value)}
              placeholder="your@email.com"
              required
              type="email"
              value={email}
            />
            {isError && (
              <p className="mb-2 text-[0.75rem] text-[var(--explore-error-text)]">
                {errorMessage}
              </p>
            )}
            <Button
              className="w-full rounded-full border-none bg-[var(--explore-accent-yellow)] font-semibold text-[0.8rem] text-[var(--explore-on-accent-text)] shadow-none transition-transform duration-150 hover:bg-[var(--explore-accent-yellow)]/90 active:scale-95 disabled:opacity-100"
              disabled={isSubmitting}
              type="submit"
            >
              {isSubmitting ? "Subscribing..." : "Subscribe"}
            </Button>
          </form>
        </>
      )}
    </div>
  );
}
